<template>
  <div class="tab-pane agrd-pane" :class="{ hidden: !active }">

    <!-- Estado vazio -->
    <div v-if="!aguardando.length" class="atencao-empty">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
      <p>Nenhuma conversa aguardando</p>
      <small>Conversas com mais de {{ props.toleranceMinutes ?? 10 }}min de espera aparecerão aqui.</small>
    </div>

    <template v-else>
      <!-- Resumo -->
      <div class="agrd-summary">
        <span class="agrd-total">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          {{ aguardando.length }} conversa{{ aguardando.length !== 1 ? 's' : '' }} aguardando há mais de {{ props.toleranceMinutes ?? 10 }}min
        </span>
      </div>

      <!-- Grid de cards -->
      <div class="agrd-grid">
        <div v-for="c in aguardando" :key="c.id" class="agrd-card">

          <!-- Fluxo tag no topo -->
          <div v-if="c.fluxo_id" class="agrd-fluxo-bar">{{ fluxoLabel(c.fluxo_id) }}</div>

          <!-- Cabeçalho: avatar + contato + ID -->
          <div class="agrd-card-head">
            <div class="agrd-av" :style="{ background: avatarColor(c.contact.name) }">
              <img v-if="c.contact.thumbnail" :src="c.contact.thumbnail" :alt="c.contact.name" style="width:100%;height:100%;object-fit:cover;border-radius:50%;display:block;" @error="(e) => (e.target as HTMLImageElement).style.display='none'" />
              <span v-else>{{ initials(c.contact.name) }}</span>
            </div>
            <div class="agrd-contact">
              <div class="agrd-name">{{ c.contact.name }}</div>
              <div v-if="c.contact.phone" class="agrd-phone">{{ c.contact.phone }}</div>
            </div>
            <span class="agrd-id">#{{ c.id }}</span>
          </div>

          <!-- Corpo: time, agente, status, canal, espera, criação -->
          <div class="agrd-card-body">

            <div class="agrd-row">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <span :class="{ 'agrd-muted': !c.team }">{{ c.team?.name || 'TypeBot' }}</span>
            </div>

            <div class="agrd-row">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <span :class="{ 'agrd-muted': !c.assignee }">{{ c.assignee?.name || '— sem agente' }}</span>
            </div>

            <div class="agrd-pills">
              <span class="status-pill" :class="c.status">
                <span class="sdot"></span>
                {{ statusLabel(c.status) }}
              </span>
              <span class="ch-pill">{{ fmtChannel(c.channel) }}</span>
            </div>

            <div v-if="waitInfo(c).cls !== 'none'" class="agrd-wait">
              <span class="wait-badge" :class="waitInfo(c).cls">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                {{ waitInfo(c).label }}
              </span>
            </div>

            <div class="agrd-created">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <span>{{ fmtDate(c.created_at) }}</span>
            </div>

            <div v-if="c.labels.length" class="agrd-labels">
              <span v-for="lbl in c.labels" :key="lbl" class="label-chip">{{ lbl.replace(/-|_/g, ' ') }}</span>
            </div>

          </div>

          <!-- Rodapé: botão de conversa -->
          <div class="agrd-card-foot">
            <button class="agrd-chat-btn" @click="requestOpenChat(c.id)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              Ver Conversa
            </button>
          </div>

        </div>
      </div>
    </template>

  </div>
</template>

<script setup lang="ts">
import { computed, watchEffect } from 'vue'
import type { Conversation } from '@/types/dashboard'
import { avatarColor, initials, fmtWait, fmtChannel, fluxoLabel } from '@/composables/useHelpers'
import { requestOpenChat } from '@/composables/useChatTrigger'

const props = defineProps<{
  conversations:    Conversation[]
  active:           boolean
  toleranceMinutes?: number
}>()

const emit = defineEmits<{ updateBadge: [count: number] }>()

const aguardando = computed(() => {
  const tolMins = Math.max(1, props.toleranceMinutes ?? 10)
  const nowSecs = Date.now() / 1000
  return props.conversations
    .filter(c =>
      (c.status === 'open' || c.status === 'pending') &&
      c.waiting_since > 0 &&
      Math.floor((nowSecs - c.waiting_since) / 60) >= tolMins
    )
    .sort((a, b) => a.waiting_since - b.waiting_since)
})

watchEffect(() => emit('updateBadge', aguardando.value.length))

function statusLabel(s: string): string {
  const map: Record<string, string> = { open: 'Aberta', resolved: 'Resolvida', pending: 'Pendente' }
  return map[s] || s
}

function waitInfo(c: Conversation) {
  return fmtWait(c.waiting_since)
}

function fmtDate(ts: number): string {
  if (!ts) return '—'
  return new Date(ts * 1000).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

</script>
